import React, {
  MouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react'

import { Popover, SxProps, ThemeProvider } from '@mui/material'

import { getTheme } from 'themes'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Icon, { IconName } from 'components_new/atoms/Icon'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip, { TooltipProps } from 'components_new/atoms/Tooltip'

export interface FilterPopoverProps {
  /** Whether the filter is active (uses dark theme when true) */
  active?: boolean
  /** Content to display inside the popover */
  children: ReactNode
  /** Whether the button should appear collapsed (hides text, shows tooltip) */
  collapsed?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
  /** Icon to display in the button */
  iconName?: IconName
  /** Whether data is loading */
  loading?: boolean
  /** Controlled click of popover */
  onClick?: (event: any) => void
  /** Controlled close of popover */
  onClose?: () => void
  /** Controlled state of popover */
  open?: boolean
  /** Width of the popover paper */
  popoverWidth?: number
  /** Which way the popover opens relative to the button (default 'bottom') */
  position?: 'top' | 'bottom'
  /** Primary label text */
  primary?: ReactNode
  /** Secondary label text (displayed below primary) */
  secondary?: ReactNode
  showActive?: boolean
  /** Size for button list item */
  size?: 'medium' | 'large'
  /** Custom sx props for the container Box */
  sx?: SxProps
  /** Tooltip placement */
  tooltipPlacement?: TooltipProps['placement']
  /** Tooltip slot props */
  tooltipSlotProps?: TooltipProps['slotProps']
  /** Title shown in tooltip when collapsed */
  tooltipTitle?: ReactNode
  /** Value shown in tooltip when collapsed (displayed bold) */
  tooltipValue?: ReactNode
}

const FilterPopover = React.forwardRef(
  (props: FilterPopoverProps, ref: React.Ref<HTMLDivElement>) => {
    const {
      active,
      children,
      collapsed,
      disabled,
      iconName = 'Filter',
      loading,
      onClick,
      onClose,
      popoverWidth,
      position = 'bottom',
      primary,
      secondary,
      showActive,
      size,
      sx,
      tooltipPlacement = 'right',
      tooltipSlotProps,
      tooltipTitle,
      tooltipValue
    } = props

    const theme = useMemo(() => {
      return getTheme(active && showActive ? 'dark' : 'light')
    }, [active, showActive])

    const invisibleOnCollapsedSx = {
      opacity: collapsed ? 0 : 1,
      transition: 'opacity 0.5s ease'
    }

    const noOverflowSx = {
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: MouseEvent<HTMLElement>) => {
      if (onClick) {
        onClick(event)
      }
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      if (onClose) {
        onClose()
      }
      setAnchorEl(null)
    }

    useEffect(() => {
      // From false -> true is not supported yet.

      if (props.open === false) {
        handleClose()
      }
    }, [props.open])

    return (
      <>
        <ThemeProvider theme={theme}>
          <Box
            ref={ref}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              ...(open && !active ? { borderColor: 'primary.main' } : null),
              ...sx
            }}
          >
            <Tooltip
              slotProps={tooltipSlotProps}
              title={
                collapsed ? (
                  <>
                    {tooltipTitle}
                    <br />
                    <b>{tooltipValue}</b>
                  </>
                ) : undefined
              }
              placement={tooltipPlacement}
            >
              <ListItemButton
                dense={true}
                disabled={disabled || loading}
                onClick={handleClick}
                sx={{ px: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Icon name={iconName} sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={primary}
                  primaryTypographyProps={{ color: 'text.secondary' }}
                  secondary={loading ? <Skeleton variant="text" /> : secondary}
                  secondaryTypographyProps={{
                    variant: 'navigation',
                    noWrap: true
                  }}
                  sx={{
                    ...invisibleOnCollapsedSx,
                    ...noOverflowSx,
                    ...(size === 'medium' ? { my: 0 } : null)
                  }}
                />
                <Icon
                  name="UnfoldMore"
                  sx={{
                    color: 'action.active',
                    fontSize: '1.25rem',
                    ml: 1,
                    ...invisibleOnCollapsedSx
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </Box>
        </ThemeProvider>

        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'left',
            vertical: position === 'top' ? 'top' : 'bottom'
          }}
          onClose={handleClose}
          open={open}
          disableScrollLock
          slotProps={
            popoverWidth
              ? {
                  paper: {
                    sx: {
                      width: popoverWidth
                    }
                  }
                }
              : undefined
          }
          transformOrigin={{
            horizontal: 0,
            vertical: position === 'top' ? 'bottom' : -4
          }}
        >
          {children}
        </Popover>
      </>
    )
  }
)

FilterPopover.displayName = 'FilterPopover'

export default FilterPopover
